import { AlertDefaults, FeatureCollectionRequest, FeatureLayerSettings, MapApi, RecipientSettings, DirectorySettings, FeatureCollection, AlertType, Feature } from 'src/app/hickory-api';
import { AlertModelService } from '../alert-model.service';
import { SourceRecord } from 'src/app/alert/models';
import { getProp, getPropCaseInsensitive, setProp } from 'src/app/shared/model/utility';

export const SourceE911 = FeatureLayerSettings.FeaturesSourceEnum.E911;
export const SourceInfoCode = FeatureLayerSettings.FeaturesSourceEnum.InfoCode;
export const SourceMyAlerts = FeatureLayerSettings.FeaturesSourceEnum.MyAlertsAppUsers;

type LayerSource = SourceRecord & FeatureLayerSettings;

export class AlertGeofencesHelper {
  // avoid circular dependency by not importing AlertFields
  static readonly FIELD_NAME = 'geoFencing';

  static hasSavedSourceRecourds(alert: AlertDefaults): boolean {
    const geofences = alert?.recipients?.geofences;
    const sourceRecords = geofences?.features?.find(f => !!f.properties?.sourceRecords)?.properties?.sourceRecords as SourceRecord[];
    return !!sourceRecords?.length;
  }

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings, mapApi: MapApi = service.mapApi) {
    const geofences = alert?.recipients?.geofences;
    let featureLayers = alert?.recipients?.featureLayers;
    // check for saved source records and context in the feature properties dictionary
    let sourceRecords = geofences?.features?.find(f => !!f.properties?.sourceRecords)?.properties?.sourceRecords as SourceRecord[];
    const savedContext = geofences?.features?.find(f => !!f.properties?.savedContext)?.properties?.savedContext;
    sourceRecords = getPropCaseInsensitive(savedContext, 'SourceRecords') ?? sourceRecords;
    sourceRecords = this.sortSourceRecords(sourceRecords);
    // if we're missing either of these values, clear the map stuff from our model
    if (!geofences || !sourceRecords?.length) {
      alert.recipients.geofences = undefined;
      alert.recipients.featureLayers = undefined;
      service.state.mapSelection = undefined;
      service.notifyFieldUpdated(AlertGeofencesHelper.FIELD_NAME);
      return;
    }

    // ROAD-9322: disallow ETN or myAlerts for polls
    if (alert?.alertType === AlertType.Poll) {
      sourceRecords = this.filterRecordsForPolls(sourceRecords);
      featureLayers = this.filterLayersForPolls(featureLayers);
    }

    this.setSelections(sourceRecords, featureLayers);
    service.state.mapSelection = { geoJSON: geofences, SourceRecords: sourceRecords, context: savedContext, };
    service.notifyFieldUpdated(AlertGeofencesHelper.FIELD_NAME);

    if (mapApi) {
      sourceRecords?.forEach(record => {
        const layer = this.parseFeatureLayer(record);
        const keys = this.parseKeys(record);
        const values = this.parseValues(record);
        const request: FeatureCollectionRequest = { geoJson: geofences, countsOnly: true, legacyGroupIds: record.LegacyGroupIds, groupIds: record.GroupIds };
        service.subscription.add(mapApi.postFeatures(keys, values, layer.FeaturesSource as any, request).subscribe(res => {
          const count = this.parseCount(res);
          record.Count = count;
          service.notifyFieldUpdated(AlertGeofencesHelper.FIELD_NAME);
        }));
      });
    }
  }

  // Elm does not return valid GeoJSON ringed polygons
  // Instead the outer ring is ringNumber=0 and the inner rings are ringNumber=1, 2, 3, etc.
  // The outer ring is what we want and the inner rings are holes
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static fixInvalidElmFeatures(features: Feature[]): Feature[] {
    if (!features?.length) return [];

    const fixedFeatures: Feature[] = [];
    const featureById = new Map<string, Feature[]>();

    // Group features by ringedPolygonId
    for (const feature of features) {
      const ringedPolygonId = feature.properties?.['ringedPolygonId'];
      if (ringedPolygonId) {
        const id = ringedPolygonId as unknown as string;
        if (!featureById.has(id)) {
          featureById.set(id, []);
        }
        featureById.get(id).push(feature);
      } else {
        fixedFeatures.push(feature);
      }
    }

    for (const [_, features] of featureById.entries()) {
      features.sort((a, b) => {
        const ringA = Number(a.properties?.['ringNumber'] ?? 0);
        const ringB = Number(b.properties?.['ringNumber'] ?? 0);
        return ringA - ringB;
      });

      const firstFeature = features[0];
      if (!firstFeature?.geometry?.coordinates) continue;

      const fixed: Feature = {
        ...firstFeature,
        properties: { ...firstFeature.properties }
      };

      // Remove ring-specific properties
      if (fixed.properties) {
        delete fixed.properties['ringNumber'];
        delete fixed.properties['ringedPolygonId'];
        delete fixed.properties['shapeType'];
      }

      // Add inner rings as holes
      for (let i = 1; i < features.length; i++) {
        const feature = features[i];
        if (feature?.geometry?.coordinates) {
          if (!Array.isArray(fixed.geometry.coordinates)) {
            fixed.geometry.coordinates = [];
          }
          fixed.geometry.coordinates.push(...feature.geometry.coordinates);
        }
      }

      fixedFeatures.push(fixed);
    }

    return fixedFeatures;
  }

  static fixFeatureCollection(featureCollection: FeatureCollection) {
    if (featureCollection == null) {
      return;
    }

    // The feature model properties object is required for deserialization so we need to make sure it is non-null.
    if (!featureCollection.spatialReference) featureCollection.spatialReference = { 'wkid': 4326 };
    featureCollection.features?.forEach((x, _i) => {
      const featureObject = x;
      const geometryProperties = getProp(featureObject?.geometry, 'properties');
      setProp(x, 'properties', geometryProperties ?? {});
    });
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    if (service.mapSelection?.geoJSON) {
      // apply/validate geofences
      const geofences = service.mapSelection.geoJSON;
      this.fixFeatureCollection(geofences);
      // save off the data that we'll need to reconstruct the map if we need to in the future. For example,
      // if we need to resend this alert or if this is a template that will be re-used later. This is kind
      // of a hack, but this is the best place we have for this data. ROAD-6020, ROAD-6591
      const firstFeature = geofences.features?.find(_ => true);
      if (firstFeature != null) {
        firstFeature.properties.sourceRecords = service.mapSelection?.SourceRecords || [];
        firstFeature.properties.savedContext = service.mapSelection?.context;
      }

      preview.recipients.geofences = geofences;
      // apply/validate feature layers
      let layers = this.getFeatureLayersFromSourceRecords(service) ?? [];

      // apply filter group selections to the layers
      layers.forEach(layer => {
        const record = service.mapSelection?.SourceRecords?.find(x => x.LayerDef?.featuresSource === layer.FeaturesSource);
        layer.FilterGroupIds = record?.GroupIds;
        layer.FilterLegacyGroupIds = record?.LegacyGroupIds;
      });

      // check multi directory layer and add the selected layers to the alert request
      layers.filter(x => x.FeaturesSource === FeatureLayerSettings.FeaturesSourceEnum.MultiDirectoryUsers)?.forEach(x => {
        let directorySettings: DirectorySettings = preview.recipients?.directories?.find(d => d.directoryId === x.DirectoryId);
        if (!directorySettings) {
          directorySettings = { directoryId: x.DirectoryId };
          if (!preview.recipients.directories) preview.recipients.directories = [];
          preview.recipients.directories.push(directorySettings)
        }
        directorySettings.geoTargetUsers = true;
      });

      // make sure that channel settings match selected feature layers (unless we're editing a template)
      if (!service.isEditingTemplate) {
        const channels = preview.channels;
        // check etn
        if (channels?.etn?.send !== true) {
          layers = layers.filter(x => x.FeaturesSource !== SourceE911);
          layers = layers.filter(x => x.FeaturesSource !== SourceInfoCode);
        } else {
          channels.etn.importUsers = !!layers.find(x => x.FeaturesSource === SourceE911);
          channels.etn.directoryUsers = !!layers.find(x => x.FeaturesSource === SourceInfoCode);
        }

        // check myAlertsApp
        if (channels?.myAlertsApp?.send !== true) {
          layers = layers.filter(x => x.FeaturesSource !== FeatureLayerSettings.FeaturesSourceEnum.MyAlertsAppUsers)
        }
      }

      preview.recipients.featureLayers = layers;
    } else {
      preview.recipients.geofences = undefined;
      preview.recipients.featureLayers = undefined;
    }
  }

  static sortSourceRecords(records: SourceRecord[]): SourceRecord[] {
    return records?.sort((a, b) => a.Label?.localeCompare(b.Label) ?? 0);
  }

  static getFeatureLayersFromSourceRecords(service: AlertModelService, selectedOnly: boolean = true): Array<FeatureLayerSettings> {
    let records = service.mapSelection?.SourceRecords?.filter(r => selectedOnly ? r.Selected === true : true);
    if (service.isPoll) records = this.filterRecordsForPolls(records);
    return records?.map(r => AlertGeofencesHelper.parseFeatureLayer(r));
  }

  static parseFeatureLayer(source: LayerSource): FeatureLayerSettings {
    if (!source) return null
    return {
      Name: getPropCaseInsensitive(source, 'Name') ?? getPropCaseInsensitive(source, 'Label'),
      FeaturesSource: getPropCaseInsensitive(source, 'FeaturesSource') ?? getPropCaseInsensitive(source.LayerDef, 'FeaturesSource'),
      Count: this.parseCount(source),
      DirectoryId: getPropCaseInsensitive(source, 'DirectoryId') ?? null,
      FilterGroupIds: getPropCaseInsensitive(source, 'GroupIds') ?? null,
      FilterLegacyGroupIds: getPropCaseInsensitive(source, 'LegacyGroupIds'),
    };
  }

  static parseCount(source: object): number {
    return Math.max(getPropCaseInsensitive(source, 'Count') ?? 0, 0);
  }

  static listKeys(record: SourceRecord): string[] {
    return Object.keys(record?.LayerDef ?? {}).filter(k => k.toLowerCase() !== 'featuressource');
  }

  static parseKeys(record: SourceRecord): string {
    const keys = this.listKeys(record);
    return keys.length ? keys.join(',') : undefined;
  }

  static parseValues(record: SourceRecord): string {
    const keys = this.listKeys(record);
    const values: string[] = [];
    keys.forEach(k => {
      const v = record[k];
      if (v) values.push(decodeURIComponent(v));
    });
    return values.length ? values.join(',') : undefined;
  }

  static isSameLayer(obj1: LayerSource, obj2: LayerSource) {
    const layer1 = this.parseFeatureLayer(obj1);
    const layer2 = this.parseFeatureLayer(obj2);
    return layer1.Name === layer2.Name && layer1.FeaturesSource === layer2.FeaturesSource;
  }

  static setSelections(records: SourceRecord[], layers: FeatureLayerSettings[]) {
    records?.forEach(r => {
      r.Selected = !!layers?.find(l => this.isSameLayer(r, l)) && r.isSourceValid !== false;
    });
  }

  static geofencesCount(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): number {
    return recipients?.geofences?.features?.length ?? 0;
  }

  static geofencesSelectedLayerCount(recipients: RecipientSettings): number {
    return recipients?.featureLayers?.length ?? 0;
  }

  static geofencesContactCount(recipients: RecipientSettings): number {
    let count = 0;
    recipients?.featureLayers?.forEach(layer => {
      count += layer.Count;
    });
    return count;
  }

  // ROAD-9322: disallow ETN or myAlerts for polls
  private static disallowedForPolls = [SourceE911, SourceInfoCode, SourceMyAlerts];

  static filterLayersForPolls(featureLayers: FeatureLayerSettings[]) {
    if (!featureLayers) return featureLayers;
    const disallow = this.disallowedForPolls;
    return featureLayers?.filter(l => !disallow.includes(l.FeaturesSource));
  }

  static filterRecordsForPolls(sourceRecords: SourceRecord[]): SourceRecord[] {
    if (!sourceRecords) return sourceRecords;
    const disallow = this.disallowedForPolls.map(s => s.toString());
    return sourceRecords?.filter(r => !disallow.includes(r.LayerDef?.featuresSource));
  }
}
